import { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Check, RefreshCw, XCircle, AlertCircle, MoreVertical, UserPlus, ChevronDown, Search } from 'lucide-react';
import { FaGoogle } from 'react-icons/fa';
import { AuthContext } from 'components/context/AuthContext';
import { useGoogleAds } from 'hooks/useGoogleAds';
import { useAds } from 'components/context/AdsContext';
import { toast } from 'react-hot-toast';
import { createPortal } from 'react-dom';

export const GoogleAdsSection = () => {
  const { auth } = useContext(AuthContext);
  const { googleUserInfo } = useAds();
  const {
    isConnected,
    loadingAccounts,
    accounts,
    fetchAccounts,
    setActiveGoogleAccount,
    disconnect,
    connectGoogleAds
  } = useGoogleAds();

  const activeId = auth.user?.google_ads_customer_id || null;
  const [showMenu, setShowMenu] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [sortedAccounts, setSortedAccounts] = useState([]);
  const menuRef = useRef(null);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const accountDropdownBtnRef = useRef();
  const accountDropdownContentRef = useRef();
  const [accountDropdownPos, setAccountDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Save functionality states
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnect();
    } finally {
      setDisconnecting(false);
      setShowMenu(false);
    }
  };

  const useAnotherAccount = async () => {
    await handleDisconnect();
    connectGoogleAds(true); // Force connection even if still connected
  };

  const fetchGoogleAccountsData = useCallback(async (force = false) => {
    try {
      await fetchAccounts(force);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      toast.error(err?.message || 'Error al obtener cuentas');
    }
  }, [fetchAccounts]);

  useEffect(() => {
    if (!isConnected) return;
    fetchGoogleAccountsData();
  }, [isConnected, fetchGoogleAccountsData]);  

  // Initialize selections from current user data - only after accounts are loaded
  useEffect(() => {
    if (auth.user && accounts.length > 0) {
      setSelectedAccountId(auth.user.google_ads_customer_id || '');
    }
  }, [auth.user, accounts.length]);

  // Check for changes when selections change
  useEffect(() => {
    const currentAccount = auth.user?.google_ads_customer_id;
    const hasAccountChanged = selectedAccountId && selectedAccountId !== currentAccount;
    setHasChanges(hasAccountChanged);
  }, [selectedAccountId, auth.user]);

  const handleSave = async () => {
    if (!selectedAccountId) return;
    
    setIsSaving(true);
    try {
      await setActiveGoogleAccount(selectedAccountId);
      toast.success('Configuración de Google Ads guardada exitosamente');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving Google Ads configuration:', error);
      toast.error('Error al guardar la configuración de Google Ads');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (accounts.length && sortedAccounts.length === 0) {
      const sorted = [...accounts].sort((a, b) => {
        const isAActive = a.customer_id === activeId;
        const isBActive = b.customer_id === activeId;
        return isBActive - isAActive;
      });
      setSortedAccounts(sorted);
    }
  }, [accounts, activeId, sortedAccounts.length]);
  

  const activeAccount = accounts?.find(a => a.customer_id === activeId);

  // Reset account search when dropdown closes
  useEffect(() => {
    if (!isAccountDropdownOpen) setAccountSearchQuery('');
  }, [isAccountDropdownOpen]);

  // Filter accounts by search (descriptive_name, customer_id)
  const filteredAccounts = accountSearchQuery.trim()
    ? accounts.filter(acc => {
        const q = accountSearchQuery.toLowerCase().trim();
        return (acc.descriptive_name || '').toLowerCase().includes(q) ||
               (acc.customer_id || '').toLowerCase().includes(q);
      })
    : accounts;

  // Handle click outside of menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    if (isAccountDropdownOpen && accountDropdownBtnRef.current) {
      const updatePosition = () => {
        if (accountDropdownBtnRef.current) {
          const rect = accountDropdownBtnRef.current.getBoundingClientRect();
          setAccountDropdownPos({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width
          });
        }
      };
      
      const debouncedUpdate = () => {
        requestAnimationFrame(updatePosition);
      };
      
      updatePosition();
      
      // Listen for scroll on all parent containers
      const scrollContainers = [];
      let parent = accountDropdownBtnRef.current.parentElement;
      while (parent) {
        if (parent.scrollHeight > parent.clientHeight || parent.scrollWidth > parent.clientWidth) {
          parent.addEventListener('scroll', debouncedUpdate, { passive: true });
          scrollContainers.push(parent);
        }
        parent = parent.parentElement;
      }
      
      // Global scroll and resize
      window.addEventListener('scroll', debouncedUpdate, { passive: true });
      window.addEventListener('resize', debouncedUpdate, { passive: true });
      
      // ResizeObserver for dynamic content changes
      const resizeObserver = new ResizeObserver(debouncedUpdate);
      if (accountDropdownBtnRef.current) {
        resizeObserver.observe(accountDropdownBtnRef.current);
      }
      
      return () => {
        scrollContainers.forEach(container => {
          container.removeEventListener('scroll', debouncedUpdate);
        });
        window.removeEventListener('scroll', debouncedUpdate);
        window.removeEventListener('resize', debouncedUpdate);
        resizeObserver.disconnect();
      };
    }
  }, [isAccountDropdownOpen]);

  // Outside click for account dropdown (exclude both button and portal content)
  useEffect(() => {
    if (!isAccountDropdownOpen) return;
    const handleClick = (e) => {
      const insideButton = accountDropdownBtnRef.current?.contains(e.target);
      const insideDropdown = accountDropdownContentRef.current?.contains(e.target);
      if (!insideButton && !insideDropdown) {
        setIsAccountDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isAccountDropdownOpen]);

  return (
    <section className="relative">
      {/* Header Section - Compact */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3 flex-1">
          {/* Logo */}
          <div className="bg-blue-500/20 border border-blue-400/30 p-2 rounded-xl flex-shrink-0 flex items-center justify-center backdrop-blur-md">
            <FaGoogle className="h-6 w-6 text-blue-300" />
          </div>
          
          {/* Title and Status */}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-white mb-1">Google Ads</h3>
            {isConnected && !loadingAccounts && (
              activeAccount ? (
                <span className="flex items-center justify-center text-green-300 bg-green-500/20 border border-green-400/30 px-2 py-1 rounded-lg text-xs backdrop-blur-md w-fit">
                  <Check className="h-3 w-3 mr-1" /> Conectado
                </span>
              ) : (
                <span className="flex items-center justify-center text-yellow-300 bg-yellow-500/20 border border-yellow-400/30 px-2 py-1 rounded-lg text-xs backdrop-blur-md w-fit">
                  <AlertCircle className="h-3 w-3 mr-1" /> Seleccioná tu cuenta
                </span>
              )
            )}
            {!isConnected && (
              <p className="text-xs text-white/60">Conecta tu cuenta de Google Ads</p>
            )}
            {isConnected && googleUserInfo?.email && (
              <p className="text-xs text-white/60 mt-1 truncate">{googleUserInfo.email}</p>
            )}
          </div>
        </div>

        {/* Actions Menu */}
        {isConnected && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 text-white/70 hover:text-white rounded-lg hover:bg-white/10 backdrop-blur-sm transition-all flex-shrink-0"
              data-testid="google-menu-button"
            >
              <MoreVertical className="h-5 w-5" />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-2 w-56 backdrop-blur-xl bg-white/15 border border-white/30 rounded-xl shadow-lg py-2 z-10">
                <button
                  onClick={() => fetchGoogleAccountsData(true)}
                  disabled={loadingAccounts}
                  className="flex w-full items-center px-4 py-2 text-sm text-white hover:bg-white/20 disabled:opacity-50 transition-all"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingAccounts ? 'animate-spin' : ''}`} />
                  Refrescar cuentas
                </button>
                <button
                  onClick={useAnotherAccount}
                  disabled={disconnecting}
                  className="flex w-full items-center px-4 py-2 text-sm text-white hover:bg-white/20 disabled:opacity-50 transition-all"
                >
                  <UserPlus className={`h-4 w-4 mr-2 ${disconnecting ? 'animate-spin' : ''}`} />
                  Usar otra cuenta
                </button>
                <button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="flex w-full items-center px-4 py-2 text-sm text-red-300 hover:bg-red-500/20 disabled:opacity-50 transition-all"
                >
                  <XCircle className={`h-4 w-4 mr-2 ${disconnecting ? 'animate-spin' : ''}`} />
                  {disconnecting ? 'Desconectando...' : 'Desconectar'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {isConnected && loadingAccounts && (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-white/50 mr-2" />
          <span className="text-white/80">Cargando cuentas...</span>
        </div>
      )}

      {isConnected && !loadingAccounts && accounts.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-white/90 mb-2">Cuenta de Google Ads</label>
          <div className="relative mb-6">
            <button
              type="button"
              ref={accountDropdownBtnRef}
              onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
              className="w-full px-3 py-2 text-left border-2 border-white/30 rounded-lg shadow-sm flex items-center justify-between text-sm backdrop-blur-md bg-white/10 hover:bg-white/15 hover:border-white/40 transition-all"
            >
              <span className="flex items-center text-white">
                {selectedAccountId
                  ? accounts.find(acc => acc.customer_id === selectedAccountId)?.descriptive_name + ' (' + selectedAccountId + ')'
                  : 'Seleccionar cuenta'}
              </span>
              <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${isAccountDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {isAccountDropdownOpen && (
              createPortal(
                <div
                  role="presentation"
                  style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 12000,
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    ref={accountDropdownContentRef}
                    role="dialog"
                    aria-label="Seleccionar cuenta de Google Ads"
                    className="backdrop-blur-xl bg-white/15 border border-white/30 rounded-xl shadow-2xl overflow-hidden"
                    style={{
                      position: 'fixed',
                      top: accountDropdownPos.top,
                      left: accountDropdownPos.left,
                      width: accountDropdownPos.width,
                      minWidth: 240,
                      padding: 0,
                      pointerEvents: 'auto',
                    }}
                  >
                  <div className="p-2 border-b border-white/20 sticky top-0 bg-white/10 backdrop-blur-sm">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
                      <input
                        type="text"
                        value={accountSearchQuery}
                        onChange={(e) => setAccountSearchQuery(e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        placeholder="Buscar cuenta por nombre o ID..."
                        className="w-full pl-9 pr-3 py-2 text-sm bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50"
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-auto py-2">
                  {filteredAccounts.length === 0 ? (
                    <div className="px-4 py-6 text-center text-white/60 text-sm">
                      No se encontraron cuentas
                    </div>
                  ) : (
                  filteredAccounts.map(acc => {
                    const isDisabled = acc.status !== 'ENABLED' || acc.is_manager;
                    return (
                      <button
                        key={acc.customer_id}
                        onMouseDown={e => {
                          if (isDisabled) return;
                          e.stopPropagation();
                          setSelectedAccountId(acc.customer_id);
                          setIsAccountDropdownOpen(false);
                        }}
                        disabled={isDisabled}
                        className={`w-full px-4 py-2 text-left flex items-center border-b border-white/10 last:border-b-0 hover:bg-white/20 focus:bg-white/20 focus:outline-none ${acc.customer_id === selectedAccountId ? 'bg-blue-500/30 font-semibold' : ''} ${isDisabled ? 'opacity-60 cursor-not-allowed' : ''} transition-all`}
                        title={
                          acc.is_manager
                            ? 'Esta cuenta es una cuenta manager, por lo que no puede ser usada para operar.'
                            : acc.status !== 'ENABLED'
                              ? 'Esta cuenta aún está en proceso de creación. Ingresa a ads.google.com para finalizarlo.'
                              : ''
                        }
                      >
                        <span className="font-medium text-white">{acc.descriptive_name} ({acc.customer_id})</span>
                        {isDisabled && (
                          <span className="ml-2 text-xs text-white/50">{acc.is_manager ? 'Manager' : 'Inactiva'}</span>
                        )}
                      </button>
                    );
                  })
                  )}
                  </div>
                  </div>
                </div>,
                document.body
              )
            )}
          </div>
        </div>
      )}

      {/* Save Button */}
      {isConnected && !loadingAccounts && accounts.length > 0 && (
        <div className="flex justify-center mt-6">
          <button
            onClick={handleSave}
            disabled={!hasChanges || !selectedAccountId || isSaving}
            className={`px-6 py-3 rounded-xl transition-all duration-300 flex items-center gap-2 
              text-sm font-medium min-w-[140px] justify-center
              ${!hasChanges || !selectedAccountId || isSaving
                ? 'bg-white/10 text-white/50 cursor-not-allowed' 
                : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl'
              }`}
          >
            {isSaving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Guardando...</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span>Guardar</span>
              </>
            )}
          </button>
        </div>
      )}

      {isConnected && !loadingAccounts && accounts.length === 0 && (
        <div className="flex flex-col items-center py-8 text-center">
          <div className="flex flex-col items-center gap-2 max-w-md">
            <p className="text-white/80 text-sm">No hay cuentas disponibles de Google Ads</p>
            <p
              className="underline text-sm cursor-pointer hover:text-white transition-colors text-white/70"
              onClick={useAnotherAccount}
            >
              ¿Prefieres usar otra cuenta de Google?
            </p>
          </div>
        </div>
      )}

      { !isConnected && (
        <button
          onClick={() => connectGoogleAds(false)}
          className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg flex items-center justify-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
        >
          <FaGoogle className="h-5 w-5" />
          <span>Conectar con Google Ads</span>
        </button>
      ) }

    </section>
  );
};
